/**
 * Focus Detection Hook
 * 
 * Uses Laplacian variance algorithm to detect image sharpness in real-time.
 * The Laplacian operator highlights edges - blurry images have low variance,
 * sharp images have high variance.
 * 
 * Algorithm:
 * 1. Downsample video frame to 320x240 for performance
 * 2. Convert to grayscale
 * 3. Apply 3x3 Laplacian kernel
 * 4. Calculate variance of the result
 * 5. Higher variance = sharper image
 * 
 * Runs at 5fps to balance responsiveness with battery life.
 * 
 * @see https://www.pyimagesearch.com/2015/09/07/blur-detection-with-opencv/
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// =============================================================================
// TYPES
// =============================================================================

export type FocusStatus = 'blurry' | 'okay' | 'sharp';

export interface FocusDetectionResult {
  /** Current focus score (higher = sharper) */
  focusScore: number;
  /** Categorized focus status */
  focusStatus: FocusStatus;
  /** Whether the image is sharp enough for capture */
  isSharpEnough: boolean;
  /** Human-readable guidance text */
  guidance: string;
}

export interface FocusDetectionConfig {
  /** Threshold below which image is considered blurry (default: 100) */
  blurryThreshold?: number;
  /** Threshold above which image is considered sharp (default: 300) */
  sharpThreshold?: number;
  /** Width to downsample to for processing (default: 320) */
  processingWidth?: number;
  /** Height to downsample to for processing (default: 240) */
  processingHeight?: number;
  /** Processing interval in ms (default: 200 = 5fps) */
  intervalMs?: number;
  /** Whether detection is enabled (default: true) */
  enabled?: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const DEFAULT_CONFIG: Required<FocusDetectionConfig> = {
  // v2: Relaxed thresholds for real-world conditions
  // Laplacian variance score - lower threshold allows more captures
  // Previous: 100 was rejecting decent images in varied lighting
  blurryThreshold: 80,
  sharpThreshold: 250,
  processingWidth: 320,
  processingHeight: 240,
  intervalMs: 200,
  enabled: true,
};

// Laplacian kernel for edge detection (3x3)
// [ 0, -1,  0]
// [-1,  4, -1]
// [ 0, -1,  0]
const LAPLACIAN_KERNEL = [
  0, -1, 0,
  -1, 4, -1,
  0, -1, 0,
];

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Convert RGB to grayscale using luminosity method
 * Y = 0.299R + 0.587G + 0.114B
 */
function rgbToGrayscale(imageData: ImageData): Uint8Array {
  const gray = new Uint8Array(imageData.width * imageData.height);
  const data = imageData.data;
  
  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    gray[j] = Math.round(
      0.299 * data[i] +      // R
      0.587 * data[i + 1] +  // G
      0.114 * data[i + 2]    // B
    );
  }
  
  return gray;
}

/**
 * Apply Laplacian kernel to grayscale image
 * Returns array of Laplacian values for variance calculation
 */
function applyLaplacian(gray: Uint8Array, width: number, height: number): Float32Array {
  const result = new Float32Array((width - 2) * (height - 2));
  let idx = 0;
  
  // Skip border pixels (kernel is 3x3)
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      let sum = 0;
      
      // Apply 3x3 kernel
      for (let ky = -1; ky <= 1; ky++) {
        for (let kx = -1; kx <= 1; kx++) {
          const pixelIdx = (y + ky) * width + (x + kx);
          const kernelIdx = (ky + 1) * 3 + (kx + 1);
          sum += gray[pixelIdx] * LAPLACIAN_KERNEL[kernelIdx];
        }
      }
      
      result[idx++] = sum;
    }
  }
  
  return result;
}

/**
 * Calculate variance of an array
 * Variance measures spread - low variance = uniform = blurry
 */
function calculateVariance(values: Float32Array): number {
  if (values.length === 0) return 0;
  
  // Calculate mean
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i];
  }
  const mean = sum / values.length;
  
  // Calculate variance
  let varianceSum = 0;
  for (let i = 0; i < values.length; i++) {
    const diff = values[i] - mean;
    varianceSum += diff * diff;
  }
  
  return varianceSum / values.length;
}

/**
 * Calculate focus score from video frame
 * Returns Laplacian variance - higher = sharper
 */
function calculateFocusScore(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  width: number,
  height: number
): number {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return 0;
  
  // Set canvas to processing size
  canvas.width = width;
  canvas.height = height;
  
  // Draw downsampled video frame
  ctx.drawImage(video, 0, 0, width, height);
  
  // Get image data
  const imageData = ctx.getImageData(0, 0, width, height);
  
  // Convert to grayscale
  const gray = rgbToGrayscale(imageData);
  
  // Apply Laplacian kernel
  const laplacian = applyLaplacian(gray, width, height);
  
  // Calculate and return variance
  return calculateVariance(laplacian);
}

/**
 * Get focus status from score
 */
function getFocusStatus(
  score: number,
  blurryThreshold: number,
  sharpThreshold: number
): FocusStatus {
  if (score < blurryThreshold) return 'blurry';
  if (score >= sharpThreshold) return 'sharp';
  return 'okay';
}

/**
 * Get guidance text for user
 */
function getGuidanceText(status: FocusStatus): string {
  switch (status) {
    case 'blurry':
      return 'Hold steady - image is blurry';
    case 'okay':
      return 'Getting sharper...';
    case 'sharp':
      return 'Looking good!';
  }
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for real-time focus detection using Laplacian variance
 * 
 * @param videoRef - Reference to the video element
 * @param config - Configuration options
 * @returns Focus detection result with score, status, and guidance
 * 
 * @example
 * ```tsx
 * const videoRef = useRef<HTMLVideoElement>(null);
 * const { focusScore, focusStatus, isSharpEnough, guidance } = useFocusDetection(videoRef);
 * 
 * return (
 *   <div>
 *     <video ref={videoRef} />
 *     <FocusIndicator status={focusStatus} guidance={guidance} />
 *   </div>
 * );
 * ```
 */
export function useFocusDetection(
  videoRef: React.RefObject<HTMLVideoElement>,
  config: FocusDetectionConfig = {}
): FocusDetectionResult {
  const {
    blurryThreshold,
    sharpThreshold,
    processingWidth,
    processingHeight,
    intervalMs,
    enabled,
  } = { ...DEFAULT_CONFIG, ...config };
  
  // State
  const [focusScore, setFocusScore] = useState(0);
  const [focusStatus, setFocusStatus] = useState<FocusStatus>('blurry');
  
  // Refs
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const intervalRef = useRef<number | null>(null);
  
  // Cleanup interval
  const cleanup = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);
  
  // Process frame
  const processFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return;
    
    // Create canvas lazily
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }
    
    const score = calculateFocusScore(
      video,
      canvasRef.current,
      processingWidth,
      processingHeight
    );
    
    setFocusScore(score);
    setFocusStatus(getFocusStatus(score, blurryThreshold, sharpThreshold));
  }, [videoRef, processingWidth, processingHeight, blurryThreshold, sharpThreshold]);
  
  // Start/stop detection based on enabled flag
  useEffect(() => {
    if (!enabled) {
      cleanup();
      return;
    }
    
    // Start interval
    intervalRef.current = window.setInterval(processFrame, intervalMs);
    
    return cleanup;
  }, [enabled, intervalMs, processFrame, cleanup]);
  
  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);
  
  return {
    focusScore,
    focusStatus,
    isSharpEnough: focusStatus !== 'blurry',
    guidance: getGuidanceText(focusStatus),
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

// FocusDetectionConfig is already exported at definition (line 39)
export { DEFAULT_CONFIG as FOCUS_DETECTION_DEFAULTS };
